import * as XLSX from 'xlsx/dist/xlsx.mini.min';
import {
  Component,
  Input,
  SimpleChanges,
  OnChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ChartType } from 'chart.js';
import { HumanizePipe } from '@app/core/pipes/humanize.pipe';

import { PivotReport, ToolbarComponent } from './toolbar/toolbar.component';
import {
  PivotTableField,
  RawPivotTableComponent,
} from '../raw-pivot-table/raw-pivot-table.component';
import { Ag1ButtonComponent } from '../ag1-button/ag1-button.component';
import { NgTemplateOutlet } from '@angular/common';
import { PivotChartComponent } from './pivot-chart/pivot-chart.component';
import { FieldDrawerComponent } from './field-drawer/field-drawer.component';
import { Ag1LoaderComponent } from '../ag1-loader/ag1-loader.component';

@Component({
  selector: 'ag-pivot-table',
  templateUrl: './pivot-table.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ToolbarComponent,
    Ag1ButtonComponent,
    NgTemplateOutlet,
    PivotChartComponent,
    FieldDrawerComponent,
    Ag1LoaderComponent,
  ],
})
export class PivotTableComponent
  extends RawPivotTableComponent
  implements OnChanges
{
  private humanizePipe = inject(HumanizePipe);

  @Input()
  possibleFields: PivotTableField[];

  @Input()
  configType: string;

  @Input()
  showFieldToggle = true;

  @Input()
  renderer: 'table' | ChartType = 'table';

  @Input()
  canSaveAsPublic = false;

  @Input()
  isLoading: boolean = true;

  showFields = false;

  chartData = {};

  constructor() {
    super();
  }

  ngOnChanges(_changes: SimpleChanges) {
    this.reRender();
  }

  reRender() {
    if (this.renderer != 'table') this.updateChart();
    else this.rePivot();
  }

  handleFieldConfigChange() {
    this.reRender();
  }

  handleConfigClick() {
    this.showFields = !this.showFields;
  }

  handleReportChange(event: PivotReport) {
    this.rows = event.rows;
    this.cols = event.cols;
    this.values = event.values;
    this.renderer = event.renderer;

    this.rowsChange.emit(this.rows);
    this.colsChange.emit(this.cols);
    this.valuesChange.emit(this.values);

    this.reRender();
  }

  updateChart() {
    const hsh = {};
    this.data.forEach((element) => {
      this.aggregateV2(
        hsh,
        element,
        0,
        [...this.rows, ...this.cols],
        this.values,
      );
    });

    this.chartData = hsh;
  }

  // mirrors the v1 cash-flow's export format
  // doesn't actually export the pivoted table, but rather the minimal data set required to make that pivot
  exportExcel(fileName: string) {
    const hsh = {};
    this.data.forEach((element) => {
      this.aggregateV2(
        hsh,
        element,
        0,
        [...this.rows, ...this.cols],
        this.values,
      );
    });

    const aggregatedHeaders = [...this.rows, ...this.cols, ...this.values];

    const minimalDataSet = [];

    minimalDataSet.push(aggregatedHeaders.map((h) => h.field));

    this.data.forEach((originalItem) => {
      const minimalItem = [];

      aggregatedHeaders.forEach((header) => {
        minimalItem.push(originalItem[header.field]);
      });

      minimalDataSet.push(minimalItem);
    });

    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(minimalDataSet);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet 1');
    XLSX.writeFile(wb, fileName + '-' + new Date().toISOString() + '.xlsx');
  }
}
